import {
  type SalesOrderAmounts,
  type SalesOrderSendConfig,
  type SalesOrderSendItem,
  serializeParams,
} from '@/lib/helpers';
import { createAction } from '@reduxjs/toolkit';
import { fetchAssortment } from './assortment';
import { api } from '@/lib/trpc/client';
// Helpers
import { LOCALE_FIN } from '@/lib/helpers';
import type { RootState, AppDispatch } from '@/app/store';
import type {
  AdditionalRichData,
  AdditionalShopData,
  SalesOrder,
} from '@/server/sales-orders/sales-orders-get';
import { z } from 'zod';
import type {
  SalesOrderComponentInput,
  SalesOrderComponentSapInput,
} from '@/server/api/routers/sales-orders';
import { type SalesOrderProduct } from '@/server/sales-orders/sales-order-products-get';
import { type SalesOrderProductColor } from '@/server/sales-orders/sales-order-product-colors-get';
import type { SalesOrderShopOrder } from '@/server/sales-orders/sales-order-shops-orders-get';
import type { SalesOrderProductItemOrderType } from '@repo/db';
import type { SalesOrderReport } from '@/server/sales-orders/sales-order-report';
import { showAlertSnackbar } from '@/lib/atoms/notifications';
import type { RouterInputs } from '@/lib/trpc/react';

function getTrcpErrorMessage(error: any) {
  const errorMessage = error?.message || error?.data?.code;
  const localizedError = LOCALE_FIN[errorMessage as keyof typeof LOCALE_FIN]
    ? LOCALE_FIN[errorMessage as keyof typeof LOCALE_FIN]
    : errorMessage;
  return localizedError;
}

export const salesOrderRequestStarted = createAction('sales-order/request/started');
export const salesOrderSaveStarted = createAction('sales-order/save/started');
export const salesOrderSaveEnded = createAction('sales-order/save/ended');
export const salesOrderRequestFailed = createAction<string | null>('sales-order/request/failed');
export const salesOrderRequestErrorPopup = createAction<{
  title: string;
  message: string;
  confirmText?: string;
  action?: () => void;
} | null>('sales-order/request/error-popup');
export const resetSalesOrderResponse = createAction('sales-order/response/reset');

export const salesOrderCreationSucceeded = createAction('sales-order/creation/succeeded');
export const createSalesOrder =
  (assortmentId: number, cb: (error?: boolean) => void) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());

    const state = getState();

    const userState = state.user;
    const { activeChain } = userState;

    return api.salesOrders.createSalesOrder
      .mutate({
        assortmentId: assortmentId,
        chain: activeChain,
      })
      .then(
        () => {
          dispatch(salesOrderCreationSucceeded());
          dispatch(salesOrderSaveEnded());
          if (cb) {
            cb();
          }
          return dispatch(fetchAssortment(assortmentId.toString()));
        },
        (error) => {
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
          dispatch(salesOrderSaveEnded());
          if (cb) {
            cb(true);
          }
        }
      );
  };

export const salesOrderFetchSucceeded = createAction<
  SalesOrder & AdditionalRichData & AdditionalShopData
>('sales-order/fetch/succeeded');
export const fetchSalesOrder =
  (orderGroupId: number, shopId: number) => (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.salesOrders.getSalesOrder
      .query({
        id: orderGroupId,
        shopId: shopId,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          if (typeof result === 'object') {
            return dispatch(salesOrderFetchSucceeded(result));
          }
          dispatch(salesOrderRequestFailed(null));
        },
        (error) => {
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
          showAlertSnackbar({
            message: `Failed to fetch sales order: ${getTrcpErrorMessage(error)}`,
            alertSeverity: 'error',
          });
        }
      );
  };

export const editSalesOrderSucceeded = createAction<SalesOrder>('sales-order/put/succeeded');

export const editSalesOrder =
  (
    salesOrderGroupId: number,
    updateData: RouterInputs['salesOrders']['editSalesOrder']['updateData'],
    shopId: number,
    cb?: (error?: boolean) => void
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());
    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.salesOrders.editSalesOrder
      .mutate({
        chain: activeChain,
        id: salesOrderGroupId,
        shopId,
        updateData,
      })
      .then(
        (results) => {
          dispatch(salesOrderSaveEnded());
          if (results.status !== 'success') {
            if (cb) {
              cb(true);
            } else {
              // Show alert popup
              dispatch(
                salesOrderRequestErrorPopup({
                  title: LOCALE_FIN.ERROR_UPDATE_TITLE,
                  message: LOCALE_FIN.ERROR_SOMETHING_WENT_WRONG,
                })
              );
            }
          } else {
            if (typeof results.result === 'object') {
              dispatch(editSalesOrderSucceeded(results.result));
            }
            if (cb) {
              cb();
            }
          }
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          const errorMessage = getTrcpErrorMessage(error);
          dispatch(salesOrderRequestFailed(errorMessage));
          if (cb) {
            cb(true);
          }

          showAlertSnackbar({
            message: errorMessage ? errorMessage : LOCALE_FIN.ERROR_SOMETHING_WENT_WRONG,
            alertSeverity: 'error',
          });
        }
      );
  };

// Hidden sizes
export const saveHiddenSizesSucceeded = createAction<SalesOrder>('sales-order/put/succeeded');
export const saveHiddenSizes =
  (
    id: number,
    assortmentProductColorId: number,
    shopId: number,
    variants: {
      assortmentProductColorId: number;
      salesOrderGroupId: number;
      b24ProductVariantId: number;
      ean: string;
    }[]
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.salesOrders.hideSalesOrderVariants
      .mutate({
        id: id,
        assortmentProductColorId: assortmentProductColorId,
        shopId: shopId,
        variants: variants,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          dispatch(salesOrderSaveEnded());
          if (result) {
            return dispatch(saveHiddenSizesSucceeded(result));
          }
          dispatch(salesOrderRequestFailed(null));
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
        }
      );
  };

// Delivery weeks

export const saveSalesOrderProductColorDeliveryDatesSucceeded = createAction<SalesOrder>(
  'sales-order/product-color-delivery-days/succeeded'
);

const saveSalesOrderProductColorDeliveryDatesInput = z.object({
  deliveryDates: z
    .object({
      deliveryDate1: z.string(),
      deliveryDate2: z.string(),
      deliveryDate3: z.string(),
      deliveryDate4: z.string(),
      deliveryDate5: z.string(),
      deliveryDate6: z.string(),
    })
    .partial()
    .refine((data) => Object.keys(data).length > 0, {
      message: 'deliveryDates must have at least one property.',
    }),
  id: z.number(),
  shopId: z.number(),
  assortmentProductColorId: z.number().nullable(),
  assortmentProductIds: z.array(z.number()),
  chain: z.enum(['intersport', 'budgetsport']),
});
export const saveSalesOrderProductColorDeliveryDates =
  ({
    deliveryDates,
    salesOrderGroupId,
    shopId,
    assortmentProductColorId = null,
    assortmentProductIds = [],
  }: {
    deliveryDates: Record<string, string>;
    salesOrderGroupId: number;
    shopId: number;
    assortmentProductColorId: number | null;
    assortmentProductIds: number[];
  }) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.salesOrders.saveSalesOrderProductColorDeliveryDates
      .mutate({
        deliveryDates,
        id: salesOrderGroupId,
        shopId,
        assortmentProductColorId,
        assortmentProductIds,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          dispatch(salesOrderSaveEnded());
          if (result) {
            dispatch(saveSalesOrderProductColorDeliveryDatesSucceeded(result));
          }
          return dispatch(fetchSalesOrder(salesOrderGroupId, shopId));
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          const errorMessage = getTrcpErrorMessage(error);
          dispatch(salesOrderRequestFailed(errorMessage));
          showAlertSnackbar({
            message: errorMessage ? errorMessage : LOCALE_FIN.ERROR_SOMETHING_WENT_WRONG,
            alertSeverity: 'error',
          });
        }
      );
  };

// Components

export const createSalesOrderComponentSucceeded = createAction<SalesOrder>(
  'sales-order/component-create/succeeded'
);
export const createSalesOrderComponent =
  (components: SalesOrderComponentInput['components'], salesOrderGroupId: number, shopId: number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.salesOrders.createSalesOrderComponent
      .mutate({
        components,
        salesOrderGroupId,
        shopId,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          dispatch(salesOrderSaveEnded());
          if (result) {
            dispatch(createSalesOrderComponentSucceeded(result));
          }
          return dispatch(fetchSalesOrder(salesOrderGroupId, shopId));
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
        }
      );
  };

export const deleteSalesOrderComponentSucceeded = createAction<SalesOrder>(
  'sales-order/component-delete/succeeded'
);
export const deleteSalesOrderComponent =
  (componentId: number, salesOrderGroupId: number, shopId: number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.salesOrders.deleteSalesOrderComponent
      .mutate({
        componentId,
        salesOrderGroupId,
        shopId,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          dispatch(salesOrderSaveEnded());
          if (result) {
            return dispatch(deleteSalesOrderComponentSucceeded(result));
          }
          dispatch(salesOrderRequestFailed(null));
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
        }
      );
  };

// Sap component creation

export const createSalesOrderComponentSapSucceeded = createAction<
  {
    status: 'success' | 'error';
    sapId: string;
    productCode: string;
  }[]
>('sales-order/component-create-sap/succeeded');
export const createSalesOrderComponentSapFailed = createAction<{
  title: string;
  data: {
    status: 'error';
    err: { errMsg: string };
  }[];
}>('sales-order/component-create-sap/failed');

type CompleteProductInput = SalesOrderComponentSapInput['data']['product'] & {
  productCode: string;
  productBrand: string;
  productName: string;
};

export const createSalesOrderComponentSap =
  (
    product: CompleteProductInput,
    component: SalesOrderComponentSapInput['data']['component'],
    salesOrderGroupId: number,
    productCode: string,
    shopId: number,
    componentId: number
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    const responseDialogTitle = `${product.productBrand} ${
      product.sapName || product.productName
    } - ${product.productCode}`;

    return api.salesOrders.createSalesOrderComponentSap
      .mutate({
        salesOrderGroupId,
        productCode,
        shopId,
        title: responseDialogTitle,
        data: { product, component },
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          dispatch(salesOrderSaveEnded());
          dispatch(createSalesOrderComponentSapSucceeded(result));
          return dispatch(fetchSalesOrder(salesOrderGroupId, shopId));
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          dispatch(
            createSalesOrderComponentSapFailed({
              title: responseDialogTitle,
              data: [{ status: 'error', err: { errMsg: getTrcpErrorMessage(error) } }],
            })
          );
        }
      );
  };

// Sales order items

/* Has error lookup for order items */
const itemsHasError = (deliveryDates: Record<string, { hasError?: boolean }> | undefined) => {
  let hasError = false;
  for (const item of Object.values(deliveryDates || {})) {
    if (item.hasError) {
      hasError = true;
      break;
    }
  }

  return hasError;
};

/* Has error lookup for config items */
const configHasError = (
  deliveryDates: Record<string, Record<string, { hasError: boolean }>> | undefined
) => {
  let hasError = false;
  for (const date of Object.values(deliveryDates || {})) {
    for (const item of Object.values(date)) {
      if (item.hasError) {
        hasError = true;
        break;
      }
    }
    if (hasError) {
      break;
    }
  }

  return hasError;
};

/* Return first item that does not have an error (and remove it from items) */
const getFirstWithoutError = <
  T extends { items: Record<string, { hasError?: boolean; [key: string]: any }> },
>(
  items: T[],
  hasError: (
    items: Record<string, { hasError?: boolean; [key: string]: any }> | undefined
  ) => boolean
): T | void => {
  let item = null;
  for (let i = 0; i < items.length; i++) {
    if (!hasError(items[i]?.items)) {
      item = items.splice(i, 1)[0];
      break; // Get only 1 item
    }
  }

  return item as T | void;
};

export const saveSalesOrderItemsSucceeded = createAction('sales-order/items-save/succeeded');
export const sendSalesOrderItemsQueue = createAction<SalesOrderSendItem[]>(
  'sales-order/items-send/queue'
);
export const sendSalesOrderItemsPending = createAction<boolean>('sales-order/items-send/pending');
export const sendSalesOrderItemsStarted = createAction('sales-order/items-send/started');
export const sendSalesOrderItemsSucceeded = createAction('sales-order/items-send/succeeded');
export const setSalesOrderInputErrors = createAction<string[]>(
  'sales-order/items-send/input-errors'
);

type SaveItemsUpdateData = {
  deliveryDate: string;
  amount: number;
  hasError?: boolean;
};

type SaveItemsUpdateIds = {
  salesOrderGroupId: number;
  shopId: number;
  assortmentProductId: number;
  assortmentProductColorId: number;
  componentId?: number;
  b24ProductVariantId?: number;
};

export const salesOrderAmountsChanged = createAction<SalesOrderAmounts>(
  'sales-order/amounts/changed'
);

export const saveSalesOrderItems =
  (updateData: SaveItemsUpdateData, updateIds: SaveItemsUpdateIds) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const salesOrderState = state.salesOrder;
    const { sendItems } = salesOrderState;

    const salesOrderAmountsState = state.salesOrderAmounts;
    const { amounts } = salesOrderAmountsState;

    const {
      salesOrderGroupId,
      shopId,
      assortmentProductId,
      assortmentProductColorId,
      componentId,
      b24ProductVariantId,
    } = updateIds;

    const { deliveryDate, amount, hasError } = updateData;

    const endpointParams: {
      salesOrderGroupId: number;
      shopId: number;
      assortmentProductColorId: number;
      componentId?: number;
      b24ProductVariantId?: number;
    } = {
      salesOrderGroupId,
      shopId,
      assortmentProductColorId,
    };
    if (componentId) {
      endpointParams.componentId = componentId;
    } else if (b24ProductVariantId) {
      endpointParams.b24ProductVariantId = b24ProductVariantId;
    }

    const endpointParamsString = serializeParams(endpointParams);

    const itemKey = componentId || b24ProductVariantId;
    if (!itemKey) {
      return;
    }

    const existingItems = amounts[assortmentProductId]?.[assortmentProductColorId]?.[itemKey] || {};
    const orderItems = {
      ...existingItems,
      [deliveryDate]: { deliveryDate, amount, hasError },
    };

    const newAmounts: SalesOrderAmounts = {
      ...amounts,
      [assortmentProductId]: {
        ...amounts[assortmentProductId],
        [assortmentProductColorId]: {
          ...amounts[assortmentProductId]?.[assortmentProductColorId],
          [itemKey]: orderItems,
        },
      },
    };

    dispatch(salesOrderAmountsChanged(newAmounts));

    const sendItemsCopy = JSON.parse(JSON.stringify(sendItems)) as SalesOrderSendItem[];

    // See if there is already existing item (if user updates the same input multiple times before we send it)
    const existingItem = sendItemsCopy.findIndex(
      (config) => config.endpointParamsString === endpointParamsString
    );
    if (existingItem !== -1) {
      // Remove this from queue
      sendItemsCopy.splice(existingItem, 1);
    }

    // Add to queue
    sendItemsCopy.push({ endpointParams, endpointParamsString, items: orderItems });

    dispatch(sendSalesOrderItemsQueue(sendItemsCopy));
  };

export const sendSalesOrderItems = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const salesOrderState = state.salesOrder;
  const { sendItems } = salesOrderState;
  const userState = state.user;
  const { activeChain } = userState;

  if (!sendItems.length) {
    return; // Nothing to send
  }

  const sendItemsCopy = JSON.parse(JSON.stringify(sendItems)) as SalesOrderSendItem[];

  // Get first item that does not have an error and remove it from queue
  const item = getFirstWithoutError(sendItemsCopy, itemsHasError);

  // Return if no item to send
  if (!item) {
    return;
  }

  dispatch(salesOrderSaveStarted());
  dispatch(sendSalesOrderItemsPending(true));
  dispatch(sendSalesOrderItemsQueue(sendItemsCopy));
  dispatch(sendSalesOrderItemsStarted());

  api.salesOrders.updateSalesOrderItems
    .mutate({
      items: item.items,
      salesOrderGroupId: item.endpointParams.salesOrderGroupId,
      shopId: item.endpointParams.shopId,
      assortmentProductColorId: item.endpointParams.assortmentProductColorId,
      componentId: item.endpointParams.componentId,
      b24ProductVariantId: item.endpointParams.b24ProductVariantId,
      chain: activeChain,
    })
    .then(
      () => {
        dispatch(salesOrderSaveEnded());
        dispatch(sendSalesOrderItemsSucceeded());
        if (!sendItemsCopy.length) {
          dispatch(sendSalesOrderItemsPending(false));
        }
      },
      (error) => {
        dispatch(salesOrderSaveEnded());
        dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
        // Show alert popup
        dispatch(
          salesOrderRequestErrorPopup({
            title: LOCALE_FIN.ERROR_PART_UPDATE_TITLE,
            message: LOCALE_FIN.ERROR_PART_UPDATE_RELOAD_PAGE,
          })
        );
      }
    );
};

// Sales order config items

export const saveSalesOrderConfigItemsSucceeded = createAction<SalesOrder>(
  'sales-order/config-items-save/succeeded'
);
export const sendSalesOrderConfigItemsQueue = createAction<SalesOrderSendConfig[]>(
  'sales-order/config-items-send/queue'
);
export const sendSalesOrderConfigItemsStarted = createAction(
  'sales-order/config-items-send/started'
);
export const sendSalesOrderConfigItemsSucceeded = createAction(
  'sales-order/config-items-send/succeeded'
);
export const setSalesOrderConfigInputErrors = createAction<string[]>(
  'sales-order/config-items-send/input-errors'
);

export const saveSalesOrderConfigItems =
  (
    updateData: {
      deliveryDate: string;
      storeRank: string;
      amount: number;
      hasError: boolean;
    },
    updateIds: {
      salesOrderGroupId: number;
      shopId: number;
      assortmentProductId: number;
      assortmentProductColorId: number;
      componentId?: number;
      b24ProductVariantId: number;
    }
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const salesOrderState = state.salesOrder;
    const { salesOrder, sendConfigs } = salesOrderState;

    const {
      salesOrderGroupId,
      shopId,
      assortmentProductId,
      assortmentProductColorId,
      componentId,
      b24ProductVariantId,
    } = updateIds;

    const { deliveryDate, storeRank, amount, hasError } = updateData;

    const endpointParams = {
      salesOrderGroupId,
      shopId,
      componentId,
      b24ProductVariantId,
      assortmentProductColorId,
    };
    const endpointParamsString = serializeParams(endpointParams);

    let configItems;
    let salesOrderCopy;

    try {
      salesOrderCopy = JSON.parse(JSON.stringify(salesOrder));

      const productIndex = salesOrderCopy.products.findIndex(
        (p: SalesOrderProduct) => p.assortmentProductId === assortmentProductId
      );

      const colorIndex = salesOrderCopy.products[productIndex].productColors.findIndex(
        (c: SalesOrderProductColor) => c.assortmentProductColorId === assortmentProductColorId
      );
      const currentConfigItems =
        salesOrderCopy.products[productIndex].productColors[colorIndex].salesOrderConfigItems[
          componentId ? 'comp' + componentId : b24ProductVariantId
        ];

      configItems = {
        ...currentConfigItems,
        [deliveryDate]: {
          ...currentConfigItems[deliveryDate],
          [storeRank]: {
            ...currentConfigItems[deliveryDate][storeRank],
            amount,
            hasError,
          },
        },
      };

      salesOrderCopy.products[productIndex].productColors[colorIndex].salesOrderConfigItems[
        componentId ? 'comp' + componentId : b24ProductVariantId
      ] = configItems;
    } catch (error) {
      dispatch(salesOrderRequestFailed(null));
    }

    const sendConfigsCopy = JSON.parse(JSON.stringify(sendConfigs));

    // See if there is already existing config (if user updates the same input multiple times before we send it)
    const existingConfig = sendConfigsCopy.findIndex(
      (config: any) => config.endpointParamsString === endpointParamsString
    );
    if (existingConfig !== -1) {
      // Remove this from queue
      sendConfigsCopy.splice(existingConfig, 1);
    }

    // Add new config to queue
    sendConfigsCopy.push({
      endpointParams,
      endpointParamsString: endpointParamsString,
      items: configItems,
    });

    dispatch(sendSalesOrderConfigItemsQueue(sendConfigsCopy));
    dispatch(saveSalesOrderConfigItemsSucceeded(salesOrderCopy));
  };

export const sendSalesOrderConfigItems =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;
    const salesOrderState = state.salesOrder;
    const { sendConfigs } = salesOrderState;

    if (!sendConfigs.length) {
      return; // Nothing to send
    }

    const sendConfigsCopy = JSON.parse(JSON.stringify(sendConfigs)) as typeof sendConfigs;

    // Get first item that does not have an error and remove it from queue
    const config = getFirstWithoutError(sendConfigsCopy, configHasError);

    // Return if no configs to send
    if (!config) {
      return;
    }

    dispatch(salesOrderSaveStarted());
    dispatch(sendSalesOrderConfigItemsQueue(sendConfigsCopy));
    dispatch(sendSalesOrderConfigItemsStarted());

    api.salesOrders.saveSalesOrderConfigItems
      .mutate({
        configItems: config.items,
        salesOrderGroupId: config.endpointParams.salesOrderGroupId,
        shopId: config.endpointParams.shopId,
        componentId: config.endpointParams.componentId,
        b24ProductVariantId: config.endpointParams.b24ProductVariantId,
        assortmentProductColorId: config.endpointParams.assortmentProductColorId,
        chain: activeChain,
      })
      .then(
        () => {
          dispatch(salesOrderSaveEnded());
          dispatch(sendSalesOrderConfigItemsSucceeded());
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          dispatch(
            salesOrderRequestErrorPopup({
              title: LOCALE_FIN.ERROR_PART_UPDATE_TITLE,
              message: `${LOCALE_FIN.ERROR_PART_UPDATE_RELOAD_PAGE}\n\nError: \n${getTrcpErrorMessage(error)}`,
            })
          );
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
        }
      );
  };

// Sales order product

export const editSalesOrderProductSucceeded = createAction<SalesOrder>(
  'sales-order/product-update/succeeded'
);
export const editSalesOrderProduct =
  (
    assortmentProductId: number,
    salesOrderGroupId: number,
    shopId: number,
    updateData: { orderType: SalesOrderProductItemOrderType }
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    api.salesOrders.editSalesOrderProduct
      .mutate({
        assortmentProductId,
        salesOrderGroupId,
        shopId,
        orderType: updateData.orderType,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          dispatch(salesOrderSaveEnded());
          if (result) {
            dispatch(editSalesOrderProductSucceeded(result));
          }
          dispatch(salesOrderRequestFailed(null));
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
        }
      );
  };

// Shops orders

export const fetchSalesOrderShopsOrdersSucceeded = createAction<SalesOrderShopOrder[]>(
  'sales-order/fetch-shops-orders/succeeded'
);
export const fetchSalesOrderShopsOrders =
  (salesOrderGroupId: number, hideLoader = false) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hideLoader) {
      dispatch(salesOrderRequestStarted());
    }

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    api.salesOrders.getSalesOrderShopOrders
      .query({
        salesOrderGroupId,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          if (result) {
            dispatch(fetchSalesOrderShopsOrdersSucceeded(result));
          }
          dispatch(salesOrderRequestFailed(null));
        },
        (error) => {
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
          showAlertSnackbar({
            message: `Failed to fetch sales order shops orders: ${getTrcpErrorMessage(error)}`,
            alertSeverity: 'error',
          });
        }
      );
  };

export const editSalesOrderShopOrderSucceeded = createAction<SalesOrder>(
  'sales-order/shop-order-update/succeeded'
);
export const editSalesOrderShopOrder =
  (
    salesOrderGroupId: number,
    shopId: number,
    orderId: number,
    updateData: { accepted?: boolean; archived?: boolean },
    cb: (error?: boolean | string | null) => void
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());
    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    api.salesOrders.editSalesOrderShopOrder
      .mutate({
        salesOrderGroupId,
        orderId,
        shopId,
        updateData,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          dispatch(salesOrderSaveEnded());
          if (result) {
            dispatch(editSalesOrderShopOrderSucceeded(result));
          }
          if (cb) {
            cb();
          }
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
          if (cb) {
            cb(true);
          } else {
            // Show alert popup
            dispatch(
              salesOrderRequestErrorPopup({
                title: LOCALE_FIN.ERROR_UPDATE_TITLE,
                message: LOCALE_FIN.ERROR_SOMETHING_WENT_WRONG,
              })
            );
          }
        }
      );
  };

export const createSalesOrderShopOrderFailed = createAction<string>(
  'sales-order/shop-order-create/failed'
);
export const createSalesOrderShopOrder =
  (
    salesOrderGroupId: number,
    shopOrders: number[],
    orderType: 'SALES_ORDER_DIRECT' | 'SALES_ORDER_WAREHOUSE'
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());
    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.salesOrders.createSalesOrderShopOrder
      .mutate({
        shopOrders,
        orderType,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          dispatch(salesOrderSaveEnded());
          dispatch(fetchSalesOrderShopsOrders(salesOrderGroupId));
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          dispatch(createSalesOrderShopOrderFailed(getTrcpErrorMessage(error)));
        }
      );
  };

// Order summary

export const fetchSalesOrderSummaryAmountsSucceeded = createAction<
  Record<string, Record<string, number>>
>('sales-order/fetch-summary-amounts/succeeded');
export const fetchSalesOrderSummaryAmounts =
  (salesOrderGroupId: number) => (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.salesOrders.getSalesOrderSummaryAmounts
      .query({
        salesOrderGroupId,
        chain: activeChain,
      })
      .then(
        ({ result }) => dispatch(fetchSalesOrderSummaryAmountsSucceeded(result)),
        (error) => dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)))
      );
  };

export const deleteSalesOrderPartsSucceeded = createAction<boolean>(
  'sales-order/sales-order-parts-delete/succeeded'
);

/**
 * Delete order parts
 *
 * @param {number} orderId
 * @returns {void}
 */
export const deleteSalesOrderParts =
  (salesOrderGroupId: number, orderId: number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    api.salesOrders.deleteSalesOrderParts
      .mutate({
        orderId,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          dispatch(salesOrderSaveEnded());
          dispatch(deleteSalesOrderPartsSucceeded(result));
          dispatch(fetchSalesOrderShopsOrders(salesOrderGroupId));
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
        }
      );
  };

/**
 * Re-send sales order item
 */
export const resendSalesOrderPart =
  (salesOrderGroupId: number, partOrderId: number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.salesOrders.resendSalesOrderPart
      .mutate({
        partOrderId,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          dispatch(salesOrderSaveEnded());
          dispatch(fetchSalesOrderShopsOrders(salesOrderGroupId));
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
        }
      );
  };

/**
 * Edit sales order shop order color
 *
 * @param {string} salesOrderGroupId - Sales order group id.
 * @param {string} shopId - Shop id.
 * @param {string} salesOrderId - Order id.
 * @param {string} assortmentProductColorId - Assortment product color id.
 * @param {object} additionalInfo - Additional info object.
 * @param {function} cb - Callback function.
 * @returns {void}
 */
export const editSalesOrderShopOrderColorAdditionalInfo =
  (
    salesOrderGroupId: number,
    shopId: number,
    salesOrderId: number,
    assortmentProductColorId: number,
    additionalInfo: string,
    cb: (error?: boolean) => void
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());
    dispatch(salesOrderSaveStarted());
    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    api.salesOrders.editSalesOrderProductColorAdditionalInfo
      .mutate({
        salesOrderGroupId,
        shopId,
        salesOrderId,
        assortmentProductColorId,
        additionalInfo,
        chain: activeChain,
      })
      .then(
        ({ result }) => {
          dispatch(salesOrderSaveEnded());
          if (result) {
            dispatch(editSalesOrderShopOrderSucceeded(result));
          } else {
            dispatch(salesOrderRequestFailed(null));
          }
          if (cb) {
            cb();
          }
        },
        (error) => {
          dispatch(salesOrderSaveEnded());
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
          if (cb) {
            cb(true);
          }
        }
      );
  };

export const getOrderReportSucceeded = createAction<SalesOrderReport>(
  'sales-order/order-report/succeeded'
);

type OrderReportSearchFilters = {
  season: string;
  brands: string[];
  productType: string;
  campaign: string;
  shopId: number;
  status: string;
  targetGroups: string[];
  category: string;
  sport: string;
  onlyUnorderedProducts: boolean;
  roles: string[];
  campaignLevelRoles: ('TOP' | 'BASIC')[];
};

/**
 * Get order report
 *
 * @param {object} searchFilters
 * @returns {void}
 */
export const getOrderReport =
  (searchFilters: OrderReportSearchFilters) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    const filters = {
      season: searchFilters.season,
      brands: searchFilters.brands ? searchFilters.brands : undefined,
      productType: searchFilters.productType,
      campaignUuid: searchFilters.campaign,
      shopId: searchFilters.shopId || undefined,
      status: searchFilters.status,
      targetGroups: searchFilters.targetGroups,
      category: searchFilters.category,
      sport: searchFilters.sport,
      zeroAmount: searchFilters.onlyUnorderedProducts,
      roles: searchFilters.roles,
      /*focusProduct: undefined as boolean | undefined,
      starProduct: undefined as boolean | undefined,
      productPotentialGood: undefined as boolean | undefined,
      productPotentialExceptional: undefined as boolean | undefined,
      affiliateProduct: undefined as boolean | undefined,*/
      campaignLevelRoles: undefined as ('TOP' | 'BASIC')[] | undefined,
    };

    /*if (searchFilters?.symbols.includes('focus')) {
      filters.focusProduct = true;
    }

    if (searchFilters?.symbols.includes('star')) {
      filters.starProduct = true;
    }

    if (searchFilters?.symbols.includes('product_potential_good')) {
      filters.productPotentialGood = true;
    }

    if (searchFilters?.symbols.includes('product_potential_exceptional')) {
      filters.productPotentialExceptional = true;
    }

    if (searchFilters?.symbols.includes('affiliate')) {
      filters.affiliateProduct = true;
    }

    if (searchFilters?.campaignLevelRoles.length) {
      filters.campaignLevelRoles = searchFilters.campaignLevelRoles;
    }*/

    return api.salesOrders.getOrderReport
      .query({
        chain: activeChain,
        filters: filters,
      })
      .then(
        ({ result }) => {
          dispatch(getOrderReportSucceeded(result));
        },
        (error) => {
          dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)));
        }
      );
  };

export const salesOrderRevisionReportFetchSucceeded = createAction<
  { data: Record<string, string>; fields: string[] }[]
>('sales-order/revision-report/succeeded');

export const salesOrderHideReportDialog = createAction('sales-order/revision-report/hide-dialog');

export const fetchSalesOrderRevisionReport =
  (requestParams: {
    salesOrderGroupId: number;
    season: string;
    quarter: string;
    brand: string;
    types: string[];
  }) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(salesOrderRequestStarted());

    const state = getState();

    const userState = state.user;
    const { user, activeChain } = userState;

    api.salesOrders.getSalesOrderRevisionReport
      .query({
        ...requestParams,
        chain: activeChain,
      })
      .then(
        // @ts-expect-error The CSV functions don't return the correct type based on csv boolean switch
        ({ result }) => dispatch(salesOrderRevisionReportFetchSucceeded(result)),
        (error) => dispatch(salesOrderRequestFailed(getTrcpErrorMessage(error)))
      );
  };
